import {
  Target, Users, PenLine, BadgeCheck, Pencil, Circle, Smartphone, Sun, Mic,
  Video, Wand2, Lightbulb, Sparkles, Compass, CheckCircle, LayoutList, Anchor,
  FileText, Search, Tag, Hash, Calendar, Layers, ClipboardList, BarChart2,
  LineChart, Eye, LucideIcon,
} from 'lucide-react';
import { Lesson, LessonColor } from '@/data/academyModules';

const ICON_MAP: Record<string, LucideIcon> = {
  Target, Users, PenLine, BadgeCheck, Pencil, Circle, Smartphone, Sun, Mic,
  Video, Wand2, Lightbulb, Sparkles, Compass, CheckCircle, LayoutList, Anchor,
  FileText, Search, Tag, Hash, Calendar, Layers, ClipboardList, BarChart2,
  LineChart, Eye,
};

const COLOR_CLASSES: Record<LessonColor, {
  icon: string;
  badge: string;
  badgeBg: string;
  headerGradient: string;
  iconGlow: string;
  accent: string;
}> = {
  orange: {
    icon: 'bg-orange-100 text-orange-600 dark:bg-orange-950/60 dark:text-orange-400',
    badge: 'text-orange-600 dark:text-orange-400',
    badgeBg: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
    headerGradient: 'from-orange-500/8 via-orange-500/3 to-transparent dark:from-orange-500/12 dark:via-orange-500/4',
    iconGlow: 'shadow-orange-500/20',
    accent: 'bg-orange-500',
  },
  blue: {
    icon: 'bg-blue-100 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400',
    badge: 'text-blue-600 dark:text-blue-400',
    badgeBg: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    headerGradient: 'from-blue-500/8 via-blue-500/3 to-transparent dark:from-blue-500/12 dark:via-blue-500/4',
    iconGlow: 'shadow-blue-500/20',
    accent: 'bg-blue-500',
  },
  green: {
    icon: 'bg-green-100 text-green-600 dark:bg-green-950/60 dark:text-green-400',
    badge: 'text-green-600 dark:text-green-400',
    badgeBg: 'bg-green-500/10 text-green-600 dark:text-green-400',
    headerGradient: 'from-green-500/8 via-green-500/3 to-transparent dark:from-green-500/12 dark:via-green-500/4',
    iconGlow: 'shadow-green-500/20',
    accent: 'bg-green-500',
  },
};

interface LessonCardProps {
  lesson: Lesson;
  index: number;
}

const LessonCard = ({ lesson, index }: LessonCardProps) => {
  const Icon = ICON_MAP[lesson.icon] ?? Target;
  const colors = COLOR_CLASSES[lesson.color];

  return (
    <div className="glass-card rounded-2xl overflow-hidden">
      {/* Header */}
      <div className={`p-4 md:p-5 border-b border-border/30 bg-gradient-to-br ${colors.headerGradient}`}>
        {/* lesson badge */}
        <div className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest mb-3 ${colors.badgeBg}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${colors.accent}`} />
          Lekcja {index + 1}
        </div>

        <div className="flex items-start gap-3.5">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md ${colors.icon} ${colors.iconGlow}`}>
            <Icon className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base md:text-lg font-extrabold text-foreground leading-snug mb-1.5">
              {lesson.title}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{lesson.summary}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div
        className="p-4 md:p-5 pt-3 lesson-content"
        dangerouslySetInnerHTML={{ __html: lesson.content }}
      />
    </div>
  );
};

export default LessonCard;
